// THE RUN STAGE VOCABULARY and the `run_stage` payload — the coarse phase a run reports
// itself to be in, closed so an untrusted box cannot push an unbounded label.
// Split out of `../task-run-event.ts`, which is now the barrel over this directory.

import { z } from 'zod';

/**
 * Every stage a run can occupy, in ladder order (terminals last) — the CLOSED
 * vocabulary shared by the box's durable stage ledger (`apps/runner/src/run-state.ts`,
 * which IMPORTS this list rather than restating it) and the `run_stage` wire event
 * below. One list, by construction: the ladder a box resumes from and the progress the
 * mothership renders can never drift into two different vocabularies.
 *
 * The ladder's MECHANICS (which edges are legal, which stages are terminal, which
 * demand a reason) stay box-side — they govern a file only the runner writes. Domain
 * owns the tokens, because those are what crosses the wire.
 */
export const RUN_STAGES = [
  'preparing',
  'surveying',
  'working',
  'pr_open',
  'handoff',
  'done',
  'blocked',
  'failed',
  /**
   * The box gave the run back (a drain) — terminal for THIS attempt and not a failure:
   * a redelivery retries it without reading a prior escalation into it. Pairs with
   * the {@link RUN_RELEASED_EVENT} terminal.
   */
  'released',
] as const;

export const RunStageSchema = z.enum(RUN_STAGES);
export type RunStage = z.infer<typeof RunStageSchema>;

/**
 * The `run_stage` status sub-event a box streams on every ACCEPTED transition of its
 * durable stage ledger (`apps/runner/src/run-state.ts` — one transition, one atomic
 * write, one event). Between `run_started` and `run_finished` a run's progress is
 * otherwise invisible to the mothership: the live view can only infer it from tool
 * calls and prose, and the next signal after that is the presence reaper's TTL. This
 * event makes the ladder an observable fact — the sixth sibling of
 * {@link PlanAuthoredPayloadSchema} / {@link KbLearningsPayloadSchema} /
 * {@link CapabilitiesDetectedPayloadSchema} / {@link ReviewPostedPayloadSchema}.
 *
 * Deliberately NOT a lifecycle transition, and the one sibling with NO write-half. A
 * stage is BOX-side progress, not mothership state: `run_started` / `run_finished` /
 * `error` remain the only events that move a task, because a box at `pr_open` has not
 * finished and one that recorded `failed` still owes the sink its terminal `error`.
 * The durable `task_run_events` row IS the deliverable (the #58 live view reads the
 * rows), so the sink detects it to LOG it and writes nothing.
 *
 * The parse is still the trust boundary: `stage`/`from` are the closed
 * {@link RUN_STAGES} vocabulary, so an untrusted box can never push an unbounded
 * string into a rendered timeline row or a mothership log line.
 *
 * No `reason` field by design — an escalation carries its cause on its OWN event (the
 * `error` kind, or a guard's `guard_tripped` status). A stage marker is a progress
 * record, not an incident report.
 */
export const RunStagePayloadSchema = z.object({
  event: z.literal('run_stage'),
  /** The stage the run just ENTERED — exactly what the ledger wrote. */
  stage: RunStageSchema,
  /**
   * The stage it left (equal to `stage` on a checkpoint — a resumed run, or a long
   * stage recording what it just learned). Optional, degrade-not-refuse: a producer
   * that knows only where it now IS still renders a row, and a missing edge is worth
   * more than a dropped event.
   */
  from: RunStageSchema.optional(),
  /** 1 on a first attempt, +1 for every attempt that adopted a prior ledger. */
  attempt: z.number().int().positive(),
});
export type RunStagePayload = z.infer<typeof RunStagePayloadSchema>;
